import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Building, Users, Globe, TrendingUp, Shield, ArrowRight, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const ServicesPage = () => {
  usePageTitle('Services');
  const { t, lang } = useLanguage();

  const services = [
    {
      id: 'individual',
      icon: FileText,
      title: lang === 'es' ? 'Declaraciones de Impuestos Individuales (1040)' : 'Individual Tax Returns (1040)',
      badge: t('mostPopular'),
      desc: lang === 'es' 
        ? 'Preparación experta de sus declaraciones federales y estatales. Maximizamos cada deducción y crédito para el que califica.'
        : 'Expert preparation of your federal and state individual tax returns. We maximize every deduction and credit you qualify for.',
      includes: lang === 'es' 
        ? ['Ingresos W-2', 'Ingresos 1099', 'Ganancias/pérdidas de inversiones', 'Deducciones detalladas', 'Créditos por hijos y familia', 'Créditos educativos', 'Todos los estados soportados']
        : ['W-2 income', '1099 income', 'Investment gains/losses', 'Itemized deductions', 'Child and family credits', 'Education credits', 'All states supported'],
      color: '#0B1F3A'
    },
    {
      id: 'business',
      icon: Building,
      title: lang === 'es' ? 'Declaraciones de Impuestos Comerciales' : 'Business Tax Returns',
      desc: lang === 'es'
        ? 'Preparación completa de impuestos para LLCs, S-Corps, C-Corps y sociedades. Manejamos la complejidad para que pueda enfocarse en su negocio.'
        : 'Complete tax preparation for LLCs, S-Corps, C-Corps, and partnerships. We handle the complexity so you can focus on your business.',
      includes: lang === 'es'
        ? ['S-Corp (Formulario 1120-S)', 'C-Corp (Formulario 1120)', 'Sociedad (Formulario 1065)', 'Declaraciones de LLC', 'Deducciones comerciales', 'Cronogramas de depreciación', 'Planificación multi-año']
        : ['S-Corp (Form 1120-S)', 'C-Corp (Form 1120)', 'Partnership (Form 1065)', 'LLC returns', 'Business deductions', 'Depreciation schedules', 'Multi-year planning'],
      color: '#2563EB'
    },
    {
      id: 'self-employed',
      icon: Users,
      title: lang === 'es' ? 'Autónomo / 1099' : 'Self-Employed / 1099',
      desc: lang === 'es'
        ? 'Preparación fiscal especializada para freelancers, contratistas y propietarios únicos. Encontramos cada deducción disponible para autónomos.'
        : 'Specialized tax preparation for freelancers, contractors, and sole proprietors. We find every deduction available to self-employed individuals.',
      includes: lang === 'es'
        ? ['Preparación del Anexo C', 'Impuesto de autoempleo', 'Deducción de oficina en casa', 'Gastos de vehículo', 'Deducción de seguro médico', 'Contribuciones de jubilación', 'Estimaciones trimestrales']
        : ['Schedule C preparation', 'Self-employment tax', 'Home office deduction', 'Vehicle expenses', 'Health insurance deduction', 'Retirement contributions', 'Quarterly estimates'],
      color: '#0B1F3A'
    },
    {
      id: 'multi-state',
      icon: Globe,
      title: lang === 'es' ? 'Declaración Multi-Estado' : 'Multi-State Filing',
      desc: lang === 'es'
        ? '¿Trabaja o vive en múltiples estados? Manejamos la complejidad de las obligaciones fiscales multi-estado, determinaciones de residencia y reglas específicas del estado.'
        : 'Work or live in multiple states? We handle the complexity of multi-state tax obligations, residency determinations, and state-specific rules.',
      includes: lang === 'es'
        ? ['Declaraciones en los 50 estados', 'Análisis de residencia', 'Optimización de créditos estatales', 'Impuestos de trabajo remoto', 'Impuestos de enfermeras viajeras', 'Planificación de domicilio']
        : ['All 50 state filings', 'Residency analysis', 'State credit optimization', 'Remote work taxes', 'Travel nurse taxes', 'Domicile planning'],
      color: '#2563EB'
    },
    {
      id: 'planning',
      icon: TrendingUp,
      title: lang === 'es' ? 'Planificación y Consultoría Fiscal' : 'Tax Planning & Consulting',
      desc: lang === 'es'
        ? 'Estrategia fiscal durante todo el año para minimizar legalmente su carga fiscal. Trabajamos proactivamente para optimizar sus decisiones financieras para eficiencia fiscal.'
        : 'Year-round tax strategy to legally minimize your tax burden. We work proactively to optimize your financial decisions for tax efficiency.',
      includes: lang === 'es'
        ? ['Consultas durante todo el año', 'Modelado de proyección fiscal', 'Asesoramiento de estructura de entidad', 'Planificación fiscal de jubilación', 'Estrategia fiscal de inversiones', 'Optimización de estructura empresarial']
        : ['Year-round consultations', 'Tax projection modeling', 'Entity structure advice', 'Retirement tax planning', 'Investment tax strategy', 'Business structure optimization'],
      color: '#0B1F3A'
    },
    {
      id: 'audit',
      icon: Shield,
      title: lang === 'es' ? 'Ayuda con Auditorías del IRS' : 'IRS Audit Help',
      desc: lang === 'es'
        ? '¿Recibió un aviso del IRS? No entre en pánico. Tenemos experiencia navegando auditorías del IRS y ayudaremos a proteger sus derechos a través del proceso.'
        : 'Received an IRS notice? Don\'t panic. We have experience navigating IRS audits and will help protect your rights through the process.',
      includes: lang === 'es'
        ? ['Revisión de avisos del IRS', 'Preparación de respuesta de auditoría', 'Representación ante el IRS', 'Negociación de acuerdos de pago', 'Liberación de embargo de salario', 'Asistencia con declaraciones atrasadas']
        : ['IRS notice review', 'Audit response preparation', 'IRS representation', 'Payment plan negotiation', 'Wage garnishment release', 'Back tax filing assistance'],
      color: '#2563EB'
    },
  ];

  const pricingNote = lang === 'es' 
    ? 'Tarifa fija según la complejidad. Precio comunicado antes de comenzar.'
    : 'Flat-fee based on complexity. Fee disclosed in advance.';

  return (
    <div className="pt-[72px]">
      {/* Hero */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0B1F3A 0%, #1E3A5F 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full" style={{ background: '#D4AF37', filter: 'blur(100px)' }} />
          <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full" style={{ background: '#2563EB', filter: 'blur(80px)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-6 text-center relative z-10">
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-6">
            {lang === 'es' ? 'Expertos Fiscales Licenciados' : 'Licensed Tax Experts'}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            {t('servicesTitle')}
          </h1>
          <p className="text-blue-200 text-lg max-w-2xl mx-auto">
            {t('servicesSub')}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc) => (
              <div key={svc.id} id={svc.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
                <div className="p-8">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: `${svc.color}10` }}>
                      <svc.icon size={28} style={{ color: svc.color }} />
                    </div>
                    {svc.badge && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ background: '#D4AF3720', color: '#B8860B' }}>
                        {svc.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-[#0B1F3A] mb-3 group-hover:text-[#2563EB] transition-colors">
                    {svc.title}
                  </h3>
                  <p className="text-[#475569] text-sm mb-5 leading-relaxed">
                    {svc.desc}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {svc.includes.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#475569]">
                        <CheckCircle size={14} className="text-[#10B981] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-5 border-t border-gray-100">
                    <Link to="/get-quote" state={{ service: svc.id }}>
                      <button className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 bg-[#0B1F3A] text-white">
                        {lang === 'es' ? 'Solicitar Cotización' : 'Request a Quote'} <ArrowRight size={16} />
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing Note */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-[#0B1F3A]/5 border border-[#0B1F3A]/10">
              <CheckCircle size={20} className="text-[#10B981]" />
              <span className="text-[#0B1F3A] font-medium">{pricingNote}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0B1F3A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            {lang === 'es' ? '¿No Está Seguro Qué Servicio Necesita?' : 'Not Sure Which Service You Need?'}
          </h2>
          <p className="text-blue-200 mb-8">
            {lang === 'es' 
              ? 'Reserve una consulta gratuita y evaluaremos su situación para recomendar el mejor enfoque.'
              : 'Book a free consultation and we\'ll assess your situation to recommend the best approach.'
            }
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment">
              <button className="px-8 py-4 font-semibold rounded-xl transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5" style={{ background: '#D4AF37', color: '#0B1F3A' }}>
                {t('ctaBook')} <ArrowRight size={16} className="inline ml-2" />
              </button>
            </Link>
            <Link to="/get-quote">
              <button className="px-8 py-4 font-semibold rounded-xl bg-white text-[#0B1F3A] transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5">
                {lang === 'es' ? 'Solicitar Cotización' : 'Request a Quote'} <ArrowRight size={16} className="inline ml-2" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
